// Pure helpers for the Team Exam settings page. Limits mirror
// validateSettingsInput on the server (myquest-server/src/services/teamExam/validation.js).

export const MAX_RATE = 1000;
export const MAX_REWARD = 100000;
export const EXAMPLE_SUBJECT_COUNTS = [1, 2, 3, 4];

const INT_RE = /^\d+$/;

export const parseWhole = (value) => {
  const text = String(value ?? "").trim();
  return INT_RE.test(text) ? Number(text) : null;
};

export function settingsToForm(settings) {
  const rewards = settings?.rewards || {};
  return {
    hostRate: String(settings?.host_rate_per_subject ?? ""),
    joinRate: String(settings?.join_rate_per_subject ?? ""),
    firstCoins: String(rewards.first?.coins ?? ""),
    secondEnabled: rewards.second?.enabled === true,
    secondCoins: String(rewards.second?.coins ?? 0),
    thirdEnabled: rewards.third?.enabled === true,
    thirdCoins: String(rewards.third?.coins ?? 0),
  };
}

function rangeError(value, min, max) {
  const n = parseWhole(value);
  if (n === null) return "Enter a whole number (digits only).";
  if (n < min || n > max) return `Enter a whole number from ${min} to ${max}.`;
  return "";
}

/** Returns an object of field -> message; empty when the form is valid. */
export function validateForm(form) {
  const errors = {};
  const check = (key, min, max) => {
    const message = rangeError(form[key], min, max);
    if (message) errors[key] = message;
  };
  check("hostRate", 1, MAX_RATE);
  check("joinRate", 1, MAX_RATE);
  check("firstCoins", 1, MAX_REWARD);
  check("secondCoins", form.secondEnabled ? 1 : 0, MAX_REWARD);
  check("thirdCoins", form.thirdEnabled ? 1 : 0, MAX_REWARD);
  return errors;
}

/** Builds the PUT body (server snake_case). Call only when validateForm is empty. */
export function formToPayload(form) {
  return {
    host_rate_per_subject: parseWhole(form.hostRate),
    join_rate_per_subject: parseWhole(form.joinRate),
    rewards: {
      first: { coins: parseWhole(form.firstCoins) },
      second: { enabled: !!form.secondEnabled, coins: parseWhole(form.secondCoins) },
      third: { enabled: !!form.thirdEnabled, coins: parseWhole(form.thirdCoins) },
    },
  };
}

export function isUnchanged(form, saved) {
  if (!saved) return true;
  return JSON.stringify(formToPayload(form)) === JSON.stringify(formToPayload(settingsToForm(saved)));
}

/** Cost of a room for N subjects at the given per-subject rate. */
export const costFor = (rate, subjects) => rate * subjects;

/** Same wording as prizeLine in the app: "1st: 10 coins · 2nd: 5 · 3rd: 2"; disabled places are hidden. */
export function prizeLine(payload) {
  const r = payload.rewards;
  const places = [
    ["1st", r.first.coins],
    ["2nd", r.second.enabled ? r.second.coins : 0],
    ["3rd", r.third.enabled ? r.third.coins : 0],
  ].filter(([, coins]) => coins > 0);
  return places
    .map(([label, coins], i) => (i === 0 ? `${label}: ${coins} coins` : `${label}: ${coins}`))
    .join(" · ");
}

/** Readable text for a failed request; the server validation text is shown as is. */
export function describeError(error) {
  const status = error?.status;
  if (status === 0) return error.message;
  if (status === 400 && error.message) return `The server rejected these values: ${error.message}`;
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 403) return "You do not have permission to change these settings (admin access required).";
  if (status === 429) return error.message || "Too many requests. Please wait a moment and try again.";
  if (status >= 500) return "The server had a problem. Reload the settings to check what is currently saved, then try again.";
  return error?.message || "Something went wrong. Please try again.";
}

/** Locale date text for a Firestore-like timestamp, or "" when unusable. */
export function formatUpdatedAt(value) {
  let ms = null;
  if (typeof value === "number") ms = value;
  else if (typeof value === "string") ms = Date.parse(value);
  else if (value && typeof value === "object") {
    const seconds = value.seconds ?? value._seconds;
    if (typeof seconds === "number") ms = seconds * 1000;
  }
  if (!Number.isFinite(ms)) return "";
  return new Date(ms).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
